// LT-179: Contact detail sidebar panel showing the contact's active
// sequence enrollment, if any. Fetched from
// `GET /contacts/<id>/active-enrollment`. Hidden when no active enrollment.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Workflow } from 'lucide-react';

import { contactSequenceAPI } from '../services/sequencesAPI';
import { sequencesAPI } from '../services/sequencesAPI';
import type { Sequence, SequenceEnrollment } from '../types/sequence';


interface Props {
  contactId: string;
}

const ContactActiveSequencePanel: React.FC<Props> = ({ contactId }) => {
  const [enrollment, setEnrollment] = useState<SequenceEnrollment | null>(null);
  const [sequence, setSequence] = useState<Sequence | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    contactSequenceAPI
      .getActiveEnrollment(contactId)
      .then(async (resp) => {
        if (cancelled) return;
        const e = resp.active_enrollment;
        setEnrollment(e);
        if (e) {
          try {
            const seq = await sequencesAPI.get(e.sequence_id);
            if (!cancelled) setSequence(seq);
          } catch {
            /* ignore — show enrollment without sequence name */
          }
        }
      })
      .catch(() => {
        /* swallow — panel hides on error */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [contactId]);

  if (loading || !enrollment) return null;

  return (
    <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 border border-white/10 rounded-xl p-4 mt-4">
      <div className="flex items-center gap-2 mb-2">
        <Workflow className="w-4 h-4 text-cyan-400" />
        <h3 className="text-sm font-medium text-white">Active sequence</h3>
      </div>
      <div className="text-sm">
        <Link
          to={`/sequences/${enrollment.sequence_id}`}
          className="text-cyan-400 hover:text-cyan-300 hover:underline"
        >
          {sequence?.name || enrollment.sequence_id.slice(0, 8) + '…'}
        </Link>
        <div className="text-xs text-gray-400 mt-1">
          On step {enrollment.current_step_position + 1} ·{' '}
          enrolled {new Date(enrollment.enrolled_at).toLocaleDateString()}
        </div>
      </div>
    </div>
  );
};


export default ContactActiveSequencePanel;
